/**
 * Phase 611 — SDR Health Banner.
 *
 * Strip-style alert at the top of the AI Agency Command Center that
 * surfaces the latest SDR sweep's misconfiguration state at a glance.
 *
 * Severities:
 *   critical → red banner   (auth/billing/key issues — operator action required)
 *   warn     → amber banner (transient / paused / stale)
 *   ok       → hidden       (no-op, healthy sweeps don't add noise)
 *
 * Phase 550m — Also renders `agency_overlay.dispatch_gaps` and
 * `escalation_gaps` as inline pills, so operational misconfig (missing
 * Meta creds, empty escalation matrix) shows up even when the SDR
 * pipeline itself is green. If SDR severity is `ok` but the overlay
 * has gaps, the banner still renders (amber) so the gaps surface.
 *
 * Polls /api/super-admin/sales-team/sdr/health-banner every 60s.
 */
import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { AlertCircle, AlertTriangle, Plug, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const POLL_MS = 60_000;

const relTime = (iso) => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function SDRHealthBanner() {
  const [state, setState] = useState(null);
  const [dismissed, setDismissed] = useState(null); // primary_reason of the dismissed banner

  const load = useCallback(async () => {
    try {
      const r = await api.get('/super-admin/sales-team/sdr/health-banner');
      setState(r.data || null);
    } catch (_e) {
      setState(null);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  if (!state) return null;

  const overlay = state.agency_overlay || { dispatch_gaps: [], escalation_gaps: [], has_gaps: false };
  const hasOverlayGaps = !!overlay.has_gaps;
  const sdrOk = state.severity === 'ok';

  // Render nothing when both SDR is healthy and there are zero overlay gaps.
  if (sdrOk && !hasOverlayGaps) return null;

  // Effective severity for the wrapper: critical SDR wins, otherwise warn.
  const effectiveSeverity = state.severity === 'critical' ? 'critical' : 'warn';

  // Dismissed gate: a dismissed `primary_reason` hides the SDR-side message.
  // But if there are overlay gaps, we ALWAYS keep the banner mounted (the
  // gaps themselves are too important to be hidden by an SDR dismissal).
  const hideSdrLine =
    dismissed && state.primary_reason && dismissed === state.primary_reason;
  if (hideSdrLine && !hasOverlayGaps) return null;

  const isCritical = effectiveSeverity === 'critical';
  const Icon = isCritical ? AlertCircle : AlertTriangle;

  const wrap = isCritical
    ? 'border-rose-300 bg-rose-50 text-rose-900'
    : 'border-amber-300 bg-amber-50 text-amber-900';
  const iconColor = isCritical ? 'text-rose-600' : 'text-amber-600';
  const headlineColor = isCritical ? 'text-rose-900' : 'text-amber-900';
  const detailColor = isCritical ? 'text-rose-800/90' : 'text-amber-800/90';
  const tagColor = isCritical
    ? 'bg-rose-100 text-rose-700'
    : 'bg-amber-100 text-amber-700';
  const testId = isCritical ? 'sdr-banner-critical' : 'sdr-banner-warn';

  const headline = hideSdrLine
    ? 'AI Agency operational gaps detected'
    : state.headline || 'AI Agency operational gaps detected';

  return (
    <div className="mx-auto max-w-7xl px-6 pt-4" data-testid="sdr-health-banner">
      <div
        className={`flex items-start gap-3 rounded-xl border-2 px-4 py-3 shadow-sm ${wrap}`}
        data-testid={testId}
      >
        <Icon className={`mt-0.5 h-5 w-5 flex-shrink-0 ${iconColor}`} />
        <div className="flex-1 min-w-0">
          {!hideSdrLine && (
            <>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span
                  className={`text-sm font-semibold ${headlineColor}`}
                  data-testid="sdr-banner-headline"
                >
                  {headline}
                </span>
                {state.primary_reason && (
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${tagColor}`}
                    data-testid="sdr-banner-reason"
                  >
                    {state.primary_reason}
                  </span>
                )}
                {state.ran_at && (
                  <span className={`text-xs ${detailColor}`}>
                    · last sweep {relTime(state.ran_at)}
                  </span>
                )}
                {state.errors_count > 0 && (
                  <span className={`text-xs ${detailColor}`}>
                    · {state.errors_count} error{state.errors_count > 1 ? 's' : ''}
                  </span>
                )}
              </div>
              {state.detail && (
                <p
                  className={`mt-1 text-xs leading-relaxed ${detailColor}`}
                  data-testid="sdr-banner-detail"
                >
                  {state.detail}
                </p>
              )}
            </>
          )}

          {/* Phase 550m — Agency operational gaps overlay */}
          {hasOverlayGaps && (
            <div
              className={`${hideSdrLine ? '' : 'mt-2 pt-2 border-t border-dashed border-current/20'}`}
              data-testid="agency-overlay-block"
            >
              <div className="flex flex-wrap items-center gap-1.5">
                <Plug className={`h-3.5 w-3.5 ${iconColor}`} />
                <span className={`text-[11px] font-semibold uppercase tracking-wider ${headlineColor}`}>
                  Operational gaps
                </span>
                {overlay.dispatch_gaps?.map((g) => (
                  <span
                    key={`dg-${g.channel}-${g.reason}`}
                    className={`rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${tagColor}`}
                    data-testid={`agency-gap-dispatch-${g.channel}`}
                    title={g.message}
                  >
                    {g.channel} · {g.reason}
                  </span>
                ))}
                {overlay.escalation_gaps?.map((g) => (
                  <span
                    key={`eg-${g.role}-${g.reason}`}
                    className={`rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${tagColor}`}
                    data-testid={`agency-gap-escalation-${g.role}`}
                    title={g.message}
                  >
                    escalation · {g.role}
                  </span>
                ))}
              </div>
              {(overlay.dispatch_gaps?.[0]?.message || overlay.escalation_gaps?.[0]?.message) && (
                <p
                  className={`mt-1 text-xs leading-relaxed ${detailColor}`}
                  data-testid="agency-overlay-detail"
                >
                  {overlay.dispatch_gaps?.[0]?.message || overlay.escalation_gaps?.[0]?.message}
                </p>
              )}
            </div>
          )}
        </div>
        {!hideSdrLine && state.primary_reason && (
          <Button
            variant="ghost"
            size="icon"
            className={`h-7 w-7 flex-shrink-0 ${headlineColor} hover:bg-white/60`}
            onClick={() => setDismissed(state.primary_reason || 'unspecified')}
            aria-label="Dismiss"
            data-testid="sdr-banner-dismiss-btn"
            title="Dismiss until the underlying issue changes"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
